import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, CalendarPlus, Bell, Share2, X, Clock, Sparkles, ArrowRight, ChevronRight } from "lucide-react";
import { API, axios } from "@/api";
import { BrandLogo } from "./BrandLogo";
import { UnifiedStatusBadge } from "./UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { ShareSheet, getShareCopy } from "./ShareSheet";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

const TIER_CONFIG = {
  gentle: {
    title: "Worth Another Look",
    gradient: "from-slate-50 to-white",
    border: "border-slate-200",
    icon: Sparkles,
    iconColor: "text-violet-500",
    accent: "violet",
  },
  moderate: {
    title: "Worth Another Look",
    gradient: "from-amber-50/60 to-white",
    border: "border-amber-200/60",
    icon: CalendarPlus,
    iconColor: "text-amber-500",
    accent: "amber",
  },
  strong: {
    title: "Don't Miss Out",
    gradient: "from-orange-50/70 to-amber-50/40",
    border: "border-orange-200/70",
    icon: Bell,
    iconColor: "text-orange-500",
    accent: "orange",
  },
  urgent: {
    title: "Last Chance!",
    gradient: "from-rose-50/70 to-pink-50/40",
    border: "border-rose-200/70",
    icon: Clock,
    iconColor: "text-rose-500",
    accent: "rose",
  },
  next_year: {
    title: "Plan for Next Year",
    gradient: "from-sky-50/60 to-indigo-50/40",
    border: "border-sky-200/60",
    icon: CalendarPlus,
    iconColor: "text-sky-500",
    accent: "sky",
  },
};

function ActionPill({ icon: Icon, label, onClick, color = "slate", active, testId }) {
  const colors = {
    red: active ? "bg-red-100 text-red-600 border-red-300" : "bg-red-50 text-red-500 border-red-200 hover:bg-red-100",
    sky: active ? "bg-sky-100 text-sky-600 border-sky-300" : "bg-sky-50 text-sky-500 border-sky-200 hover:bg-sky-100",
    violet: active ? "bg-violet-100 text-violet-600 border-violet-300" : "bg-violet-50 text-violet-500 border-violet-200 hover:bg-violet-100",
    emerald: active ? "bg-emerald-100 text-emerald-600 border-emerald-300" : "bg-emerald-50 text-emerald-500 border-emerald-200 hover:bg-emerald-100",
    slate: "bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100 hover:text-slate-500",
  };
  return (
    <motion.button
      whileTap={{ scale: 0.92 }}
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[10px] font-semibold transition-colors ${colors[color]}`}
      data-testid={testId}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </motion.button>
  );
}

export default function WorthAnotherLook({ daysUntilBirthday, onToggleFavorite, favorites = [] }) {
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const { mode } = useTheme();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [reminded, setReminded] = useState(new Set());
  const [shareOffer, setShareOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/deals/worth-another-look`)
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDismiss = async (businessId) => {
    setDismissed((prev) => new Set([...prev, businessId]));
    axios.post(`${API}/deals/dismiss-missed`, { business_id: businessId }).catch(() => {});
    toast("Removed — we won't show this again", { duration: 2000 });
  };

  const handleRemind = async (businessId, businessName) => {
    setReminded((prev) => new Set([...prev, businessId]));
    const res = await axios.post(`${API}/deals/remind-next-year`, { business_id: businessId }).catch(() => null);
    if (res?.data?.already_saved) {
      toast("Already saved for next year!", { duration: 2000 });
    } else {
      toast(`We'll remind you about ${businessName} next year`, { duration: 3000 });
    }
  };

  const handleFavorite = (businessId) => {
    onToggleFavorite?.(businessId);
  };

  if (loading || !data || !data.deals?.length) return null;

  const visibleDeals = data.deals.filter((d) => !dismissed.has(d.business_id));
  if (visibleDeals.length < 2) return null;

  const tier = data.reminder_tier?.tier || "gentle";
  const config = TIER_CONFIG[tier] || TIER_CONFIG.gentle;
  const TierIcon = config.icon;
  const reminderMessage = data.reminder_tier?.message;
  const isBirthdayPassed = daysUntilBirthday != null && daysUntilBirthday < 0;
  const isBirthdaySoon = daysUntilBirthday != null && daysUntilBirthday >= 0 && daysUntilBirthday <= 14;

  const nextYearCount = data.next_year_count || 0;

  return (
    <section className="px-4 mb-6" data-testid="worth-another-look-section">
      <div className={`rounded-2xl border ${config.border} bg-gradient-to-br ${config.gradient} p-4 shadow-sm`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <TierIcon className={`w-5 h-5 ${config.iconColor}`} />
            <h2 className="text-base font-bold text-slate-800" data-testid="wal-title">{config.title}</h2>
            {nextYearCount > 0 && (
              <span className="text-[10px] font-bold bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded-full" data-testid="wal-next-year-count">
                {nextYearCount} saved
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 font-medium">{visibleDeals.length} deals</span>
        </div>

        {/* Escalating message */}
        {reminderMessage && (tier === "moderate" || tier === "strong" || tier === "urgent" || tier === "next_year") && (
          <p className={`text-xs mb-3 leading-relaxed ${
            tier === "urgent" ? "text-rose-600 font-semibold" :
            tier === "strong" ? "text-orange-600 font-medium" :
            tier === "next_year" ? "text-sky-600 font-medium" :
            "text-amber-600"
          }`} data-testid="wal-message">
            {reminderMessage}
          </p>
        )}

        {/* Horizontal scroll of missed deal cards */}
        <div className="overflow-x-auto -mx-1 px-1 pb-1">
          <div className="flex gap-3">
            <AnimatePresence>
              {visibleDeals.map((deal) => {
                const isFav = favorites.includes(deal.business_id);
                const isReminded = reminded.has(deal.business_id) || deal.has_reminder;

                return (
                  <motion.div
                    key={deal.business_id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8, x: -20 }}
                    className="min-w-[220px] max-w-[220px] bg-white rounded-xl p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.06)] border border-slate-100/80 flex-shrink-0 relative"
                    data-testid={`wal-card-${deal.business_id}`}
                  >
                    {/* Dismiss button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDismiss(deal.business_id); }}
                      className="absolute top-2 right-2 w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
                      data-testid={`wal-dismiss-${deal.business_id}`}
                    >
                      <X className="w-3 h-3 text-slate-400" />
                    </button>

                    {/* "Saved from last year" badge for resurfaced reminders */}
                    {deal.is_saved_for_next_year && (
                      <div className="mb-2 flex items-center gap-1 px-2 py-1 rounded-md bg-sky-50 border border-sky-200 w-fit" data-testid={`wal-next-year-badge-${deal.business_id}`}>
                        <Bell className="w-3 h-3 text-sky-500" />
                        <span className="text-[10px] font-semibold text-sky-600">You saved this last year</span>
                      </div>
                    )}

                    {/* Card content */}
                    <div
                      className="flex items-start gap-2.5 mb-2.5 cursor-pointer"
                      onClick={() => navigate(`/offer/${deal.business_id}`)}
                    >
                      <BrandLogo name={deal.name} logoUrl={deal.logo_url} size="sm" />
                      <div className="flex-1 min-w-0 pr-4">
                        <h3 className="text-sm font-bold text-slate-800 leading-tight truncate">{deal.name}</h3>
                        {getStatus(deal.business_id) && <UnifiedStatusBadge status={getStatus(deal.business_id)} />}
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">{deal.offer_title || deal.category}</p>
                        {deal.offer_value_est > 0 && (
                          <span className="text-sm font-bold text-green-600 mt-0.5 block">~${deal.offer_value_est}</span>
                        )}
                      </div>
                    </div>

                    {/* Action pills */}
                    <div className="flex flex-wrap gap-1.5">
                      <ActionPill
                        icon={Heart}
                        label={isFav ? "Saved" : "Save"}
                        onClick={(e) => { e.stopPropagation(); handleFavorite(deal.business_id); }}
                        color="red"
                        active={isFav}
                        testId={`wal-save-${deal.business_id}`}
                      />
                      <ActionPill
                        icon={isBirthdayPassed ? Clock : CalendarPlus}
                        label={isBirthdayPassed ? "Next Year" : (isBirthdaySoon ? "Add to Plan" : "Plan It")}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isBirthdayPassed) {
                            handleRemind(deal.business_id, deal.name);
                          } else {
                            navigate(`/my-itinerary?add=${deal.business_id}`);
                          }
                        }}
                        color="sky"
                        testId={`wal-plan-${deal.business_id}`}
                      />
                      <ActionPill
                        icon={Bell}
                        label={isReminded ? "Reminded" : "Remind Me"}
                        onClick={(e) => { e.stopPropagation(); handleRemind(deal.business_id, deal.name); }}
                        color="violet"
                        active={isReminded}
                        testId={`wal-remind-${deal.business_id}`}
                      />
                      <ActionPill
                        icon={Share2}
                        label="Share"
                        onClick={(e) => { e.stopPropagation(); setShareOffer(deal); }}
                        color="emerald"
                        testId={`wal-share-${deal.business_id}`}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {shareOffer && (
        <ShareSheet
          isOpen={!!shareOffer}
          onClose={() => setShareOffer(null)}
          url={`${window.location.origin}/offer/${shareOffer.business_id}`}
          title={getShareCopy(mode, "offer", { name: shareOffer.name, offerTitle: shareOffer.offer_title || "" }).title}
          description={getShareCopy(mode, "offer", { name: shareOffer.name, offerTitle: shareOffer.offer_title || "" }).description}
          businessId={shareOffer.business_id}
          businessName={shareOffer.name}
          shareContext="offer"
        />
      )}
    </section>
  );
}
